/**
 * Property calculation utilities
 * Extracted from property page for better code organization
 */

const MIN_LIST_PRICE = 50000;
const MAX_LIST_PRICE = 50000000;

function hasListingAmount(listing) {
  if (!listing || typeof listing !== "object") return false;
  const raw = listing.amount ?? listing.listPrice ?? listing.price;
  const n = Number(raw);
  return Number.isFinite(n) && n >= MIN_LIST_PRICE && n <= MAX_LIST_PRICE;
}

function getListingEpoch(listing) {
  if (!listing || typeof listing !== "object") return 0;
  const rawDate =
    listing.seriesListingDate || listing.listingDate || listing.statusDate;
  if (!rawDate) return 0;
  const epoch = Date.parse(rawDate);
  return Number.isFinite(epoch) ? epoch : 0;
}

/**
 * Latest row in `property.listings` by series/listing/status date (tie: later array index wins).
 * Does not use `property.listing` — API data is expected on `listings` only.
 * @param {Object|null|undefined} property
 * @returns {Object|null}
 */
export function getLatestListingFromListings(property) {
  const listings = property?.listings;
  if (!Array.isArray(listings) || !listings.length) return null;
  return listings.reduce((best, cur) => {
    if (!cur) return best;
    if (!best) return cur;
    const eb = getListingEpoch(best);
    const ec = getListingEpoch(cur);
    if (ec > eb) return cur;
    if (ec < eb) return best;
    return cur;
  }, null);
}

/** MLS summary row: `squareFeet` lives on `property.listing`, not on `listings[]` rows. */
function pickSquareFeetFromListingKey(property) {
  const n = Number(property?.listing?.squareFeet);
  return Number.isFinite(n) && n > 1 ? n : null;
}

function pickSquareFeetFromRoot(property) {
  if (!property || typeof property !== "object") return null;
  const n = (v) => {
    const x = Number(v);
    return Number.isFinite(x) && x > 1 ? x : null;
  };
  return (
    n(property.squareFeet) ??
    n(property.buildingSquareFeet) ??
    n(property.livingArea) ??
    n(property.livingSquareFeet) ??
    n(property.grossLivingArea) ??
    n(property.buildingArea) ??
    n(property.grossSquareFeet)
  );
}

/**
 * Sqft for display / merging: `property.listing.squareFeet` first, then root fields.
 * Returns null when neither applies (no heuristic default).
 * @param {Object|null|undefined} property
 * @returns {number|null}
 */
export function getResolvedSquareFeet(property) {
  const fromListing = pickSquareFeetFromListingKey(property);
  if (fromListing != null) return fromListing;
  return pickSquareFeetFromRoot(property);
}

/**
 * Latest listings[] row with a valid list price (for AVM / present value sync).
 * @param {Object|null|undefined} property
 * @returns {Object|null}
 */
export function resolvePrimaryListing(property) {
  if (!property || typeof property !== "object") return null;

  const listings = property.listings;
  if (!Array.isArray(listings) || !listings.length) return null;

  const listingsWithAmount = listings.filter((l) => l && hasListingAmount(l));
  if (!listingsWithAmount.length) return null;

  return listingsWithAmount.reduce((latest, current) => {
    if (!latest) return current;
    return getListingEpoch(current) > getListingEpoch(latest)
      ? current
      : latest;
  }, null);
}

function resolvePrimaryListingOrListingKey(property) {
  const fromListings = resolvePrimaryListing(property);
  if (fromListings) return fromListings;
  const listingKey = property?.listing;
  if (listingKey && hasListingAmount(listingKey)) return listingKey;
  return null;
}

/**
 * @param {Object|null|undefined} listing
 * @returns {number|null}
 */
export function getListingListPrice(listing) {
  if (!listing || typeof listing !== "object") return null;
  const raw = listing.amount ?? listing.listPrice ?? listing.price;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < MIN_LIST_PRICE || n > MAX_LIST_PRICE) return null;
  return Math.round(n);
}

/**
 * Sync property.estimatedValue from listing list price when available (mutates in place).
 * Call after loading property details so present value / AVM fields stay aligned with MLS.
 * @param {Object|null|undefined} property
 */
export function applyListingPriceToProperty(property) {
  if (!property || typeof property !== "object") return;
  const lp = getListingListPrice(resolvePrimaryListingOrListingKey(property));
  const current = Number(property.estimatedValue);
  const currentSafe = Number.isFinite(current) ? Math.round(current) : null;
  if (lp != null && (currentSafe == null || lp > currentSafe)) {
    property.estimatedValue = lp;
  }
}

/**
 * Helper function to determine building square feet with smart fallbacks
 * @param {Object} property - The property object
 * @param {Object} [_listingIgnored] - Deprecated; sqft comes from `listings[]` only
 * @returns {number} - Calculated building square feet
 */
export const getBuildingSquareFeet = (property, _listingIgnored) => {
  const direct = getResolvedSquareFeet(property);
  if (direct != null) return direct;

  // Next: estimate from assessed/estimated value and typical price per sqft
  const estValue =
    property?.estimatedValue ||
    property?.assessedValue ||
    property?.marketValue;
  if (estValue && estValue > 50000) {
    // Assume typical residential price range $150-$400 per sqft
    // Use $250 as middle estimate for calculation
    const estimatedSqft = Math.round(estValue / 250);
    if (estimatedSqft >= 500 && estimatedSqft <= 20000) {
      return estimatedSqft;
    }
  }

  // Fourth try: reasonable default based on property type
  const propertyType = property?.propertyType || property?.landUse;
  if (propertyType && propertyType.toLowerCase().includes("single family")) {
    return 1800; // Typical single family home
  }
  if (propertyType && propertyType.toLowerCase().includes("condo")) {
    return 1200; // Typical condo
  }
  if (propertyType && propertyType.toLowerCase().includes("townhouse")) {
    return 1500; // Typical townhouse
  }

  // Final fallback
  return 1500;
};

/**
 * Helper function to determine estimated value with smart fallbacks
 * @param {Object} property - The property object
 * @param {Object} [_listingIgnored] - Deprecated; list price comes from `listings[]` only
 * @returns {number} - Calculated estimated value
 */
export const getSmartEstimatedValue = (property, _listingIgnored) => {
  const primary = resolvePrimaryListingOrListingKey(property);
  const fromPrimary = getListingListPrice(primary);
  const currentEstimated = Number(property?.estimatedValue);
  const currentEstimatedSafe = Number.isFinite(currentEstimated)
    ? Math.round(currentEstimated)
    : null;
  if (fromPrimary != null) {
    if (currentEstimatedSafe != null && fromPrimary <= currentEstimatedSafe) {
      return currentEstimatedSafe;
    }
    return fromPrimary;
  }

  const amount = primary
    ? primary.amount ?? primary.listPrice ?? primary.price
    : undefined;
  let { estimatedValue } = { ...property };

  if (estimatedValue && estimatedValue > 50000) {
    return estimatedValue;
  }

  // Try different value sources in order of preference
  const valueSources = [
    property?.oneMonthValue, // Most recent market estimate
    property?.sixMonthValue, // 6-month market estimate
    property?.oneYearValue, // 1-year market estimate
    property?.threeYearValue, // 3-year trend
    property?.fiveYearValue, // 5-year trend
    property?.marketValue, // General market value
    property?.appraisedValue, // Official appraisal
    property?.assessedValue, // Tax assessment
    amount, // Listing amount
    property?.lastSaleAmount, // Most recent sale
  ];

  // Find the first reasonable value
  for (const value of valueSources) {
    if (value && value >= 50000 && value <= 50000000) {
      // Reasonable range
      // If it's a recent sale, adjust for appreciation
      if (value === property?.lastSaleAmount && property?.lastSaleDate) {
        const saleDate = new Date(property.lastSaleDate);
        const now = new Date();
        const yearsAgo = (now - saleDate) / (1000 * 60 * 60 * 24 * 365);

        // Apply modest appreciation (3% annually) if sale was recent
        if (yearsAgo > 0 && yearsAgo < 10) {
          const appreciatedValue = value * Math.pow(1.03, yearsAgo);
          return Math.round(appreciatedValue);
        }
      }
      return value;
    }
  }

  // If we have square footage, estimate based on area and property type
  const sqft = getBuildingSquareFeet(property);
  if (sqft > 100) {
    const propertyType = property?.propertyType || property?.landUse || "";
    const location = property?.city || property?.county || "";

    // Base price per sqft by property type
    let basePricePerSqft = 200; // default

    if (propertyType.toLowerCase().includes("single family")) {
      basePricePerSqft = 250;
    } else if (propertyType.toLowerCase().includes("condo")) {
      basePricePerSqft = 300;
    } else if (propertyType.toLowerCase().includes("townhouse")) {
      basePricePerSqft = 275;
    }

    // Adjust for high-value areas (basic heuristic)
    const highValueAreas = [
      "miami",
      "beach",
      "manhattan",
      "francisco",
      "angeles",
      "seattle",
      "boston",
    ];
    if (
      highValueAreas.some((area) => location.toLowerCase().includes(area))
    ) {
      basePricePerSqft *= 2;
    }

    // Check if there's lot value to add
    let lotValue = 0;
    if (property?.lotSquareFeet && property.lotSquareFeet > 5000) {
      // Large lot adds value
      lotValue = Math.min((property.lotSquareFeet - 5000) * 5, 200000);
    }

    return Math.round(sqft * basePricePerSqft + lotValue);
  }

  // Final fallback based on property characteristics
  const propertyType = property?.propertyType || property?.landUse || "";
  if (propertyType.toLowerCase().includes("single family")) {
    return 400000; // Typical single family home
  } else if (propertyType.toLowerCase().includes("condo")) {
    return 300000; // Typical condo
  }

  return 350000; // Conservative default
};

/**
 * Calculate estimated rent amount
 * @param {number} estimatedValue - The estimated property value
 * @param {number} existingRent - The existing rent amount if available
 * @returns {number} - Calculated monthly rent
 */
export const calculateEstimatedRent = (estimatedValue, existingRent = 0) => {
  const calculatedRent = Math.ceil((estimatedValue * 0.045) / 12);
  return calculatedRent > existingRent ? calculatedRent : existingRent;
};

/**
 * Calculate market value per square foot from nearby properties
 * @param {Array} properties - Array of nearby properties
 * @returns {number} - Market value per sqft
 */
export const calculateMarketValuePerSqft = (properties) => {
  if (!properties || properties.length === 0) return 0;

  const topProperties = properties.slice(0, Math.ceil(properties.length / 10));
  
  let totalValue = 0;
  let totalSqft = 0;

  topProperties.forEach((property) => {
    totalValue += property.estimatedValue || 0;
    totalSqft += getBuildingSquareFeet(property) || 0;
  });

  return totalSqft > 0 ? Math.ceil(totalValue / totalSqft) : 0;
};

